import { useCallback, useEffect, useState } from "react";
import { DesignTokens, Row, Col, Button, UndoIcon, Select } from "melco-ui";

import { useTranslation } from "react-i18next";
import { isEmpty, some } from "lodash-es";

import { LicencesListFiltersType } from "../../hooks/licenses/LicencesListFiltersType";
import { ProductsFilter } from "./filter/ProductsFilter";
import { RefferalsFilter } from "./filter/RefferalsFilter";
import { SubscriptionLevelFilter } from "./filter/SubscriptionLevelFilter";
import { Search } from "../search/Search";
import styled from "styled-components";
import { DownloadLicensesButton } from "./DownloadLicenseButton";
import { DEFAULT_FILE_FORMAT } from "../../helper/commonHelper";
import { VersionsFilter } from "./filter/VersionsFilter";
import { StatusTypeFilter } from "./filter/StatusTypeFilter";
import { useRolesContext } from "../../hooks/roles/useRolesContext";

const { spacings } = DesignTokens;
const SelectBoxWrapper = styled.div`
  max-width: 300px;
  min-width: 225px;
`;

type LicencesListFilterProps = {
  onFiltersChange: (filters: LicencesListFiltersType | undefined) => void;
  batchNumberForNewGeneratedLicense: string;
};

export const LicencesListFilter: React.FC<LicencesListFilterProps> = ({
  onFiltersChange,
  batchNumberForNewGeneratedLicense,
}) => {
  const { t } = useTranslation();
  const { roles } = useRolesContext();
  const [licencesFilter, setLicencesFilter] = useState<
    LicencesListFiltersType | undefined
  >(undefined);

  const [searchValue, setSearchValue] = useState("");
  const [userNameValue, setUserNameValue] = useState("");

  const [serialNumberValue, setSerialNumberValue] = useState("");
  const [batchNumberValue, setBatchNumberValue] = useState("");
  //const [stateSwitchValue, setStateSwitchValue] = useState(false);
  const [selectedVersionValue, setSelectedVersionValue] = useState();

  useEffect(() => {
    onFiltersChange(licencesFilter);
  }, [licencesFilter, onFiltersChange]);

  const resetFilter = useCallback(() => {
    setSearchValue("");
    setUserNameValue("")
    setSerialNumberValue("");
    setBatchNumberValue("");
    //setStateSwitchValue(false);
    setLicencesFilter(undefined);
  }, []);

  useEffect(() => {
    setBatchNumberValue(batchNumberForNewGeneratedLicense);
    setLicencesFilter((prevFilter) => {
      return { ...prevFilter, batchNumber: batchNumberForNewGeneratedLicense };
    });
  }, [batchNumberForNewGeneratedLicense]);

  const clearProductsFilter = async () => {
    setSelectedVersionValue(undefined);
    setLicencesFilter((prevFilter) => {
      return {
        ...prevFilter,
        versionId: undefined,
        subscriptionLevel: undefined,
        products: undefined,
      };
    });
  };

  const clearVersionFilter = async () => {
    setSelectedVersionValue(undefined);
    setLicencesFilter((prevFilter) => {
      return {
        ...prevFilter,
        versionId: undefined,
        subscriptionLevel: undefined,
      };
    });
  };

  return (
    <div>
      <Row gutter={[spacings.middle, spacings.middle]}>
        <Col xs={24} xl={6}>
          <Search
            onSearchInputChange={(value) => {
              setLicencesFilter((prevFilter) => {
                return { ...prevFilter, searchText: value };
              });
            }}
            onChange={(e) => setSearchValue(e.target.value)}
            value={searchValue}
          />
        </Col>
        <Col xs={24} xl={6}>
          <Search
            onSearchInputChange={(value) => {
              setLicencesFilter((prevFilter) => {
                return { ...prevFilter, userName: value };
              });
            }}
            onChange={(e) => setUserNameValue(e.target.value)}
            value={userNameValue}
            placeholder="Username"
          />
        </Col>
        <Col xs={24} xl={6}>
          <Search
            onSearchInputChange={(value) => {
              setLicencesFilter((prevFilter) => {
                return { ...prevFilter, serialNumber: value };
              });
            }}
            onChange={(e) => setSerialNumberValue(e.target.value)}
            value={serialNumberValue}
            placeholder={t("licenses.basic.search.serial.title")}
          />
        </Col>
        <Col xs={24} xl={6}>
          <Search
            onSearchInputChange={(value) => {
              setLicencesFilter((prevFilter) => {
                return { ...prevFilter, batchNumber: value };
              });
            }}
            onChange={(e) => setBatchNumberValue(e.target.value)}
            value={batchNumberValue}
            placeholder={t("licenses.basic.search.batch.title")}
          />
        </Col>
        <Col xs={24} xl={6}>
          <ProductsFilter
            onChange={async (value) => {
              await clearProductsFilter();
              setLicencesFilter((prevFilter) => {
                return {
                  ...prevFilter,
                  products: !isEmpty(value) ? value : undefined,
                };
              });
            }}
            value={licencesFilter?.products ?? undefined}
            onClear={clearProductsFilter}
          />
        </Col>
        <Col xs={24} xl={6}>
          <div>
            {licencesFilter?.products ? (
              <VersionsFilter
                selectedProductValue={licencesFilter?.products}
                onChange={async (callBackObj) => {
                  await clearVersionFilter();
                  setSelectedVersionValue(
                    !isEmpty(callBackObj.label) ? callBackObj.label : undefined
                  );
                  setLicencesFilter((prevFilter) => {
                    return {
                      ...prevFilter,
                      versionId: !isEmpty(callBackObj.value)
                        ? callBackObj.value
                        : undefined,
                    };
                  });
                }}
                onClear={clearVersionFilter}
                value={licencesFilter?.versionId ?? undefined}
              />
            ) : (
              <SelectBoxWrapper>
                <Select
                  allowClear
                  placeholder={t("licenses.basic.search.version.title")}
                  style={{ width: "100%" }}
                  maxTagCount={1}
                  options={[]}
                />
              </SelectBoxWrapper>
            )}
          </div>
        </Col>
        <Col xs={24} xl={6}>
          <div>
            {licencesFilter?.products && licencesFilter?.versionId ? (
              <SubscriptionLevelFilter
                selectedProductValue={licencesFilter?.products}
                selectedVersionValue={selectedVersionValue}
                onChange={(value) => {
                  setLicencesFilter((prevFilter) => {
                    return {
                      ...prevFilter,
                      subscriptionLevel: !isEmpty(value) ? value : undefined,
                    };
                  });
                }}
                value={licencesFilter?.subscriptionLevel ?? undefined}
              />
            ) : (
              <SelectBoxWrapper>
                <Select
                  allowClear
                  placeholder={t("licenses.basic.search.level.title")}
                  style={{ width: "100%" }}
                  maxTagCount={1}
                  options={[]}
                />
              </SelectBoxWrapper>
            )}
          </div>
        </Col>
        <Col xs={24} xl={6}>
          <RefferalsFilter
            onChange={(value) => {
              setLicencesFilter((prevFilter) => {
                // console.log({ value, prevFilter })
                return {
                  ...prevFilter,
                  refferal: !isEmpty(value) ? value : undefined,
                };
              });
            }}
            value={licencesFilter?.refferal ?? undefined}
          />
        </Col>
        <Col xs={24} xl={6}>
          <StatusTypeFilter
            onChange={(value) => {
              setLicencesFilter((prevFilter) => {
                return {
                  ...prevFilter,
                  typeList: !isEmpty(value) ? value : undefined,
                };
              });
            }}
            value={licencesFilter?.typeList ?? undefined}
          />
        </Col>
        <Col xs={24} xl={6}>
          <Row gutter={[spacings.middle, spacings.middle]}>
            <Col>
              {roles.VIRTUAL_SECURITY_DOWNLOAD && (
                <DownloadLicensesButton
                  licensesFilename={"licenses" + DEFAULT_FILE_FORMAT}
                  licencesFilter={licencesFilter}
                />
              )}
            </Col>

            <Col>
              {some(licencesFilter) && (
                <Button
                  icon={<UndoIcon />}
                  type="primary"
                  onClick={resetFilter}
                  data-cy-id="licenses-filter-reset"
                  style={{ minWidth: "100%", marginLeft: "auto" }}
                >
                  {t("filter.reset.button", { ns: "melco-common" })}
                </Button>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {/* </RowWrapper> */}
    </div>
  );
};
